import { Component, ViewChild } from '@angular/core';
import { BehaviorSubject, debounceTime, firstValueFrom } from 'rxjs';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { MasterService } from '../../../../shared/services/master.service';
import { ToasterService } from '../../../../shared/services/toaster.service';
import { MARKETPLACE_OPTIONS, Marketplace } from '../../enums/marketplace.enum';
import { CarrierMarketplaceMappingViewModel } from '../../models/view-models/carrier-marketplace-mapping-view.model';
import { CarrierMarketplaceMappingMapperService } from '../../services/carrier-marketplace-mapping-mapper.service';
import { CarrierMarketplaceMappingsApiService } from '../../services/carrier-marketplace-mappings-api.service';
import { AddEditCarrierMarketplaceMappingComponent } from '../add-edit-carrier-marketplace-mapping/add-edit-carrier-marketplace-mapping.component';

@Component({
  selector: 'app-carrier-marketplace-mappings-listing',
  standalone: false,
  templateUrl: './carrier-marketplace-mappings-listing.component.html',
  styleUrl: './carrier-marketplace-mappings-listing.component.css',
})
export class CarrierMarketplaceMappingsListingComponent {
  @ViewChild('addEditComponent') addEditComponent!: AddEditCarrierMarketplaceMappingComponent;

  companyId: number = 0;
  mappings: CarrierMarketplaceMappingViewModel[] = [];
  filteredMappings: CarrierMarketplaceMappingViewModel[] = [];
  selectedMapping: CarrierMarketplaceMappingViewModel = new CarrierMarketplaceMappingViewModel();
  loading: boolean = false;
  search: string = '';
  searchSubject = new BehaviorSubject<string>('');

  constructor(
    private readonly mappingsApiService: CarrierMarketplaceMappingsApiService,
    private readonly mappingMapperService: CarrierMarketplaceMappingMapperService,
    private readonly toastService: ToasterService,
    private readonly masterService: MasterService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    const userInfo = this.masterService.getUserDetailFromStorage();
    this.companyId = Number(userInfo.companyId);

    this.searchSubject.pipe(debounceTime(350)).subscribe((searchTerm) => {
      this.applyLocalFilter(searchTerm);
    });

    this.loadMappings();
  }

  loadMappings(): void {
    this.loading = true;
    const queryParams: Record<string, string | number | boolean> = {
      CompanyId: this.companyId,
    };

    firstValueFrom(this.mappingsApiService.getCarrierMarketplaceMappings(queryParams))
      .then((response) => {
        this.mappings = this.mappingMapperService.buildViewsFromResponse(response ?? []);
        this.applyLocalFilter(this.search);
      })
      .catch((error) => {
        this.errorHandlerService.handleApiError(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  handleSearch(searchTerm: string): void {
    this.search = searchTerm;
    this.searchSubject.next(searchTerm);
  }

  applyLocalFilter(searchTerm: string): void {
    const term = (searchTerm ?? '').trim().toLowerCase();

    if (!term) {
      this.filteredMappings = [...this.mappings];
      return;
    }

    this.filteredMappings = this.mappings.filter((mapping) => {
      const marketplace = this.getMarketplaceLabel(mapping.marketplace).toLowerCase();
      return (
        mapping.carrierName.toLowerCase().includes(term) ||
        marketplace.includes(term) ||
        mapping.shippingName.toLowerCase().includes(term) ||
        mapping.shippingCode.toLowerCase().includes(term)
      );
    });
  }

  getMarketplaceLabel(marketplace: Marketplace): string {
    return (
      MARKETPLACE_OPTIONS.find((option) => option.value === marketplace)?.label ?? 'Unknown'
    );
  }

  openAddEditForm(id: number): void {
    this.addEditComponent.initializeRecord(id);
  }

  handleTriggerFromChild(): void {
    this.loadMappings();
  }

  selectRecordToDelete(mapping: CarrierMarketplaceMappingViewModel): void {
    this.selectedMapping = mapping;
  }

  deleteRecord(): void {
    this.mappingsApiService.deleteCarrierMarketplaceMapping(this.selectedMapping.id).subscribe({
      next: () => {
        this.loadMappings();
        this.toastService.success('Carrier marketplace mapping has been deleted successfully!');
      },
      error: (err) => {
        this.errorHandlerService.handleApiError(err);
      },
    });
  }
}
